import dayjs from "dayjs";
import { sorted } from "helpers";
import type { PerformanceTimeseriesBucket } from "src/store/api/generatedApi";

const floorToInterval = (isoDate: string, intervalSeconds: number): number => {
  const ms = dayjs(isoDate).valueOf();
  const intervalMs = intervalSeconds * 1000;
  return Math.floor(ms / intervalMs) * intervalMs;
};

export const aggregateBuckets = (
  buckets: readonly PerformanceTimeseriesBucket[],
  bucketSizeSeconds: number
): readonly PerformanceTimeseriesBucket[] => {
  if (buckets.length === 0) return buckets;

  const groups = new Map<string, PerformanceTimeseriesBucket[]>();
  for (const bucket of buckets) {
    const key = `${floorToInterval(bucket.bucket_start, bucketSizeSeconds)}|${bucket.missing_data_kind ?? ""}`;
    const group = groups.get(key);
    if (group) {
      group.push(bucket);
    } else {
      groups.set(key, [bucket]);
    }
  }

  const intervalMs = bucketSizeSeconds * 1000;
  const aggregatedBuckets: PerformanceTimeseriesBucket[] = [];

  for (const [, group] of groups) {
    const firstBucket = group[0];
    if (!firstBucket) continue;
    const startMs = floorToInterval(
      firstBucket.bucket_start,
      bucketSizeSeconds
    );
    const totalVerified = group.reduce(
      (sum, bucket) => sum + bucket.total_verified_events,
      0
    );
    const matched = group.reduce(
      (sum, bucket) => sum + bucket.matched_events,
      0
    );
    const totalLabeller = group.reduce(
      (sum, bucket) => sum + bucket.total_labeller_events,
      0
    );
    const totalEventDurationSeconds = group.reduce(
      (sum, bucket) => sum + (bucket.total_event_duration_seconds ?? 0),
      0
    );
    const eventDurationCount = group.reduce(
      (sum, bucket) => sum + bucket.event_duration_count,
      0
    );
    const falseNegatives = totalVerified - matched;
    const falsePositives = totalLabeller - matched;
    const jaccardDenominator = matched + falseNegatives + falsePositives;

    aggregatedBuckets.push({
      total_event_duration_seconds:
        eventDurationCount > 0 ? totalEventDurationSeconds : null,
      event_duration_count: eventDurationCount,
      bucket_start: dayjs(startMs).toISOString(),
      bucket_end: dayjs(startMs + intervalMs).toISOString(),
      total_verified_events: totalVerified,
      matched_events: matched,
      total_labeller_events: totalLabeller,
      fn_rate: totalVerified > 0 ? falseNegatives / totalVerified : null,
      fp_rate: totalLabeller > 0 ? falsePositives / totalLabeller : null,
      jaccard_accuracy:
        jaccardDenominator > 0 ? matched / jaccardDenominator : null,
      missing_data_kind: firstBucket.missing_data_kind,
    });
  }

  return sorted(aggregatedBuckets, (bucket) =>
    dayjs(bucket.bucket_start).valueOf()
  );
};
